import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { date, money, today } from '../../utils/format';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DateInput, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';

/**
 * Charges and taxes — the rules a price is built from.
 *
 * Every product names a tax group and a pricing rule, and a pricing rule names
 * a making charge and a wastage. All four existed in the database and could be
 * chosen on the product form, but nothing in the app could create one — so a
 * shop was stuck with whatever setup laid down, and a shop whose setup laid
 * down nothing had empty dropdowns and no way to fill them.
 *
 * Nothing here is ever deleted. A rule that has priced an invoice is part of how
 * that invoice was worked out; it is retired, and stops being offered.
 */

interface Base {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  version: number;
}
interface TaxGroup extends Base {
  hsn_code: string | null;
  applies_to: 'GOODS' | 'SERVICE';
}
interface Slab {
  fromWt: string;
  toWt?: string;
  ratePerGram: string;
}
interface MakingRule extends Base {
  mode: string;
  value: string | null;
  slabs: Slab[] | null;
  slab_marginal: boolean | null;
  min_amount: string | null;
  max_amount: string | null;
}
interface WastageRule extends Base {
  mode: string;
  value: string | null;
}
interface PricingRule extends Base {
  rate_basis: string;
  applies_to: string;
  making_charge_rule_id: string | null;
  wastage_rule_id: string | null;
}

const TABS = [
  { key: 'tax', label: 'GST rates', icon: '🧾', blurb: 'The GST a piece is billed at. Products point at one of these.' },
  { key: 'making', label: 'Making charges', icon: '🔨', blurb: 'What the work on a piece costs — per gram, per piece, a percentage, or by weight slab.' },
  { key: 'wastage', label: 'Wastage', icon: '⚖️', blurb: 'The metal added for wastage, as a percentage or a fixed weight.' },
  { key: 'pricing', label: 'Pricing rules', icon: '🏷️', blurb: 'A making charge and a wastage, put together. Each product uses one.' },
] as const;

const MAKING_MODES = [
  { value: 'PER_GRAM_NET', label: '₹ per gram (net weight)' },
  { value: 'PER_GRAM_GROSS', label: '₹ per gram (gross weight)' },
  { value: 'PER_FINE_GRAM', label: '₹ per gram of pure metal' },
  { value: 'FLAT_PER_PIECE', label: 'Fixed ₹ per piece' },
  { value: 'PCT_OF_METAL_VALUE', label: '% of the metal value' },
  { value: 'PCT_OF_TOTAL', label: '% of the total' },
  { value: 'SLAB', label: 'By weight slabs' },
  { value: 'NONE', label: 'No making charge' },
];
const WASTAGE_MODES = [
  { value: 'PCT_OF_NET', label: '% of net weight' },
  { value: 'PCT_OF_FINE', label: '% of pure weight' },
  { value: 'FLAT_GRAMS', label: 'Fixed grams per piece' },
  { value: 'TOUCH_UPLIFT', label: 'Touch uplift (%)' },
  { value: 'NONE', label: 'No wastage' },
];
const label = (list: { value: string; label: string }[], v: string | null | undefined): string =>
  list.find((m) => m.value === v)?.label ?? v ?? '';

const CODE_CHARS = /[A-Z0-9\-_]/;

/* ═══════════════════════════════ the page ═══════════════════════════════ */

export function ChargesPage() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab')) ?? TABS[0]).key;
  const current = TABS.find((t) => t.key === tab)!;

  return (
    <>
      <PageHeader
        title="Charges and taxes"
        subtitle="GST rates, making charges, wastage and the pricing rules that combine them."
      />

      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams({ tab: t.key })}
            className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition ${
              tab === t.key
                ? 'border-gold-500 bg-gold-50 shadow-card'
                : 'border-silver-100 bg-surface hover:border-gold-300 hover:bg-gold-50/40'
            }`}
          >
            <span className="text-xl leading-none">{t.icon}</span>
            <span className={`text-2xs leading-tight ${tab === t.key ? 'font-semibold text-gold-700' : 'text-ink-soft'}`}>
              {t.label}
            </span>
          </button>
        ))}
      </div>

      <div className="mb-3 flex items-start gap-2">
        <span className="text-2xl leading-none">{current.icon}</span>
        <div>
          <h2 className="text-base font-semibold">{current.label}</h2>
          <p className="text-sm text-ink-soft">{current.blurb}</p>
        </div>
      </div>

      {tab === 'tax' ? <TaxTab /> : null}
      {tab === 'making' ? <MakingTab /> : null}
      {tab === 'wastage' ? <WastageTab /> : null}
      {tab === 'pricing' ? <PricingTab /> : null}
    </>
  );
}

/* ═══════════════════════════ shared pieces ══════════════════════════════ */

/** The list on the left, the form on the right — the same shape as Lists. */
function Layout({ list, form }: { list: ReactNode; form: ReactNode }) {
  return <div className="grid gap-5 lg:grid-cols-[1fr_22rem]">{list}{form}</div>;
}

function Row({
  row,
  detail,
  aside,
  onEdit,
  onRetire,
  mayEdit,
}: {
  row: Base;
  detail: ReactNode;
  aside?: ReactNode;
  onEdit: () => void;
  onRetire: () => void;
  mayEdit: boolean;
}) {
  // Retiring is this app's delete, and deleting is the owner's call.
  const mayRetire = usePermission().can('document.void');
  return (
    <div
      className={`flex items-center justify-between rounded-lg border border-silver-100 px-3 py-2.5 ${
        row.is_active ? 'bg-surface shadow-card' : 'bg-silver-50/60 opacity-80'
      }`}
    >
      <div className="min-w-0">
        <p className="truncate font-medium">{row.name}</p>
        <p className="num text-2xs text-ink-soft">
          {row.code} · {detail}
          {row.is_active ? '' : ' · retired'}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {aside}
        {mayEdit ? (
          <>
            {row.is_active ? (
              <button className="text-2xs text-gold-700 underline" onClick={onEdit}>
                Edit
              </button>
            ) : null}
            {mayRetire ? (
              <button className="text-2xs text-ink-soft underline" onClick={onRetire}>
                {row.is_active ? 'Retire' : 'Bring back'}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function RowList<T extends Base>({
  loading,
  rows,
  empty,
  render,
}: {
  loading: boolean;
  rows: T[];
  empty: string;
  render: (r: T) => ReactNode;
}) {
  if (loading) return <Loading rows={4} />;
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-silver-400 bg-surface px-6 py-10 text-center">
        <p className="font-medium">Nothing here yet</p>
        <p className="mt-1 text-sm text-ink-soft">{empty}</p>
      </div>
    );
  }
  const live = rows.filter((r) => r.is_active !== false);
  const retired = rows.filter((r) => r.is_active === false);
  return (
    <div>
      <div className="space-y-2">{live.map(render)}</div>
      {retired.length > 0 ? (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-ink-soft">
            {retired.length} retired {retired.length === 1 ? 'entry' : 'entries'}
          </summary>
          <div className="mt-2 space-y-2">{retired.map(render)}</div>
        </details>
      ) : null}
    </div>
  );
}

/** Retire / bring back, with the same wording everywhere. */
function useRetire(route: string, queryKey: string, setError: (e: unknown) => void) {
  const client = useQueryClient();
  const [target, setTarget] = useState<Base | null>(null);
  const mutation = useMutation({
    mutationFn: (row: Base) => api.patch(`${route}/${row.id}`, { is_active: !row.is_active }, row.version),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [queryKey] });
      setTarget(null);
    },
    onError: (e) => {
      setError(e);
      setTarget(null);
    },
  });
  const dialog = (
    <ConfirmDialog
      open={!!target}
      tone={target?.is_active === false ? 'primary' : 'danger'}
      title={target?.is_active === false ? `Use ${target?.name ?? ''} again?` : `Retire ${target?.name ?? ''}?`}
      message={
        target?.is_active === false
          ? 'It will be offered again on products and new entries.'
          : 'Invoices already priced with it keep exactly what they were charged. It simply stops being offered.'
      }
      confirmLabel={target?.is_active === false ? 'Bring it back' : 'Retire it'}
      busy={mutation.isPending}
      onCancel={() => setTarget(null)}
      onConfirm={() => target && mutation.mutate(target)}
    />
  );
  return { ask: setTarget, dialog };
}

function FormCard({
  title,
  onSubmit,
  busy,
  ready,
  editing,
  onCancel,
  submitLabel,
  children,
}: {
  title: string;
  onSubmit: () => void;
  busy: boolean;
  ready: boolean;
  editing: boolean;
  onCancel: () => void;
  submitLabel: string;
  children: ReactNode;
}) {
  const onEnter = useKeyboardForm();
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (ready && !busy) onSubmit();
  };
  return (
    <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="card h-fit space-y-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
      <div className="flex gap-2 pt-1">
        <button className="btn-primary flex-1" disabled={busy || !ready}>
          {busy ? 'Saving…' : editing ? 'Save changes' : submitLabel}
        </button>
        {editing ? (
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

function CodeField({ value, onChange, editing, placeholder }: { value: string; onChange: (v: string) => void; editing: boolean; placeholder: string }) {
  return editing ? (
    <p className="text-2xs text-ink-soft">
      Code <span className="num">{value}</span> stays as it is — products already point at it.
    </p>
  ) : (
    <Field label="Code" required hint="Short, in capitals.">
      <TextInput value={value} onChange={onChange} uppercase maxLength={32} allow={CODE_CHARS} placeholder={placeholder} />
    </Field>
  );
}

/* ═══════════════════════════════ GST ════════════════════════════════════ */

function TaxTab() {
  const { can } = usePermission();
  const mayEdit = can('master.manage');
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<TaxGroup | null>(null);
  const [history, setHistory] = useState<TaxGroup | null>(null);
  const blank = { code: '', name: '', hsn: '7113', appliesTo: 'GOODS', rate: '3', from: today() };
  const [form, setForm] = useState(blank);

  const list = useQuery({ queryKey: ['tax-groups'], queryFn: () => api.get<{ data: TaxGroup[] }>('/tax-groups?limit=200') });
  const rates = useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => api.get<{ data: { tax_group_id: string; rate_pct: string | null; effective_from: string }[] }>('/tax-rates'),
  });
  const rateOf = new Map((rates.data?.data ?? []).map((r) => [r.tax_group_id, r]));

  const reset = (): void => {
    setForm(blank);
    setEditing(null);
  };
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['tax-groups'] });
    void client.invalidateQueries({ queryKey: ['tax-rates'] });
    void client.invalidateQueries({ queryKey: ['taxGroups'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const header = {
        name: form.name.trim(),
        hsn_code: form.hsn.trim() || null,
        applies_to: form.appliesTo,
      };
      if (editing) {
        await api.patch(`/tax-groups/${editing.id}`, header, editing.version);
        // A changed rate is a new version from the chosen date, never an edit
        // of the old one: last month's invoices were billed at last month's rate.
        const current = rateOf.get(editing.id)?.rate_pct;
        if (current === undefined || Number(current ?? 0) !== Number(form.rate || 0)) {
          await api.post(`/tax-groups/${editing.id}/rates`, { effectiveFrom: form.from, ratePct: form.rate || '0' });
        }
        return;
      }
      const created = await api.post<{ id: string }>('/tax-groups', {
        code: form.code.trim().toUpperCase(),
        ...header,
        hsn_code: form.hsn.trim() || undefined,
      });
      await api.post(`/tax-groups/${created.id}/rates`, { effectiveFrom: form.from, ratePct: form.rate || '0' });
    },
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      refresh();
      reset();
      setError(null);
    },
    onError: setError,
  });
  const retire = useRetire('/tax-groups', 'tax-groups', setError);

  const rows = list.data?.data ?? [];
  const ready = form.name.trim() !== '' && (editing !== null || form.code.trim() !== '') && form.from !== '';

  return (
    <>
      <ErrorNotice error={error ?? list.error} />
      <Layout
        list={
          <RowList
            loading={list.isLoading}
            rows={rows}
            empty="Add the GST rate your jewellery is billed at — 3% for gold and silver jewellery."
            render={(r) => {
              const rate = rateOf.get(r.id);
              return (
                <Row
                  key={r.id}
                  row={r}
                  mayEdit={mayEdit}
                  detail={`${r.applies_to === 'SERVICE' ? 'Service' : 'Goods'}${r.hsn_code ? ` · HSN ${r.hsn_code}` : ''}`}
                  aside={
                    <button className="text-right" onClick={() => setHistory(r)} title="Rate history">
                      <span className="num block text-sm font-semibold">{rate ? `${Number(rate.rate_pct ?? 0)}%` : 'no rate'}</span>
                      {rate ? <span className="block text-2xs text-ink-faint">from {date(rate.effective_from)}</span> : null}
                    </button>
                  }
                  onEdit={() => {
                    setEditing(r);
                    setForm({
                      code: r.code,
                      name: r.name,
                      hsn: r.hsn_code ?? '',
                      appliesTo: r.applies_to,
                      rate: String(Number(rate?.rate_pct ?? 0)),
                      from: today(),
                    });
                  }}
                  onRetire={() => retire.ask(r)}
                />
              );
            }}
          />
        }
        form={
          mayEdit ? (
            <FormCard
              title={editing ? `Edit “${editing.name}”` : 'Add a GST rate'}
              onSubmit={() => save.mutate()}
              busy={save.isPending}
              ready={ready}
              editing={!!editing}
              onCancel={reset}
              submitLabel="Add GST rate"
            >
              <Field label="Name" required>
                <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={120} placeholder="Jewellery 3%" />
              </Field>
              <CodeField value={form.code} onChange={(v) => setForm({ ...form, code: v })} editing={!!editing} placeholder="GST3" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="GST rate" required hint="Split into CGST + SGST for you.">
                  <NumberInput value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} decimals={2} max={99} suffix="%" />
                </Field>
                <Field label={editing ? 'New rate from' : 'Applies from'} required>
                  <DateInput value={form.from} onChange={(v) => setForm({ ...form, from: v })} allowFuture />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="HSN / SAC">
                  <TextInput value={form.hsn} onChange={(v) => setForm({ ...form, hsn: v })} maxLength={8} allow={/[0-9]/} placeholder="7113" />
                </Field>
                <Field label="For">
                  <Select
                    value={form.appliesTo}
                    onChange={(v) => setForm({ ...form, appliesTo: v || 'GOODS' })}
                    options={[
                      { value: 'GOODS', label: 'Goods' },
                      { value: 'SERVICE', label: 'Service (job work)' },
                    ]}
                  />
                </Field>
              </div>
              {editing ? (
                <p className="text-2xs text-ink-soft">
                  Changing the rate starts a new rate on the date above. Invoices before it keep the old rate.
                </p>
              ) : null}
            </FormCard>
          ) : null
        }
      />
      {retire.dialog}
      {history ? <TaxHistory group={history} onClose={() => setHistory(null)} /> : null}
    </>
  );
}

function TaxHistory({ group, onClose }: { group: TaxGroup; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['tax-history', group.id],
    queryFn: () =>
      api.get<{
        data: {
          id: string;
          effective_from: string;
          effective_to: string | null;
          components: { code: string; ratePct: string; jurisdiction: string }[];
        }[];
      }>(`/tax-groups/${group.id}/rates`),
  });
  return (
    <ConfirmDialog
      open
      title={`${group.name} — rate history`}
      confirmLabel="Close"
      cancelLabel="Close"
      onConfirm={onClose}
      onCancel={onClose}
      message={
        q.isLoading ? (
          'Loading…'
        ) : (q.data?.data ?? []).length === 0 ? (
          'No rate has been set for this group yet, so it bills at zero. Edit it to set one.'
        ) : (
          <ul className="space-y-1.5 text-left">
            {(q.data?.data ?? []).map((v) => {
              const intra = v.components.filter((c) => c.jurisdiction === 'INTRA');
              const total = intra.reduce((s, c) => s + Number(c.ratePct), 0);
              return (
                <li key={v.id} className="flex justify-between gap-4 text-sm">
                  <span>
                    {date(v.effective_from)} – {v.effective_to ? date(v.effective_to) : 'now'}
                  </span>
                  <span className="num font-medium">
                    {total}%{' '}
                    <span className="text-2xs text-ink-soft">
                      {intra.map((c) => `${c.code} ${Number(c.ratePct)}`).join(' + ')}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )
      }
    />
  );
}

/* ═══════════════════════════ making charges ═════════════════════════════ */

function MakingTab() {
  const { can } = usePermission();
  const mayEdit = can('master.manage');
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<MakingRule | null>(null);
  const blank = {
    code: '',
    name: '',
    mode: 'PER_GRAM_NET',
    value: '',
    min: '',
    max: '',
    slabs: [{ fromWt: '0', toWt: '', ratePerGram: '' }] as { fromWt: string; toWt: string; ratePerGram: string }[],
    marginal: false,
  };
  const [form, setForm] = useState(blank);

  const list = useQuery({ queryKey: ['making-rules'], queryFn: () => api.get<{ data: MakingRule[] }>('/making-rules?limit=200') });
  const reset = (): void => {
    setForm(blank);
    setEditing(null);
  };

  const isSlab = form.mode === 'SLAB';
  const isPct = form.mode === 'PCT_OF_METAL_VALUE' || form.mode === 'PCT_OF_TOTAL';
  const needsValue = form.mode !== 'NONE' && !isSlab;

  const save = useMutation({
    mutationFn: () => {
      const slabs = isSlab
        ? form.slabs
            .filter((s) => s.ratePerGram !== '')
            .map((s) => ({ fromWt: s.fromWt || '0', ...(s.toWt ? { toWt: s.toWt } : {}), ratePerGram: s.ratePerGram }))
        : null;
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        mode: form.mode,
        // The column is never empty: no-charge and slab rules carry zero.
        value: needsValue ? form.value : '0',
        slabs,
        slab_marginal: form.marginal,
        min_amount: form.min || null,
        max_amount: form.max || null,
      };
      if (editing) return api.patch(`/making-rules/${editing.id}`, body, editing.version);
      // Create takes "absent" rather than null for the optional parts.
      const create = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null));
      return api.post('/making-rules', { code: form.code.trim().toUpperCase(), ...create });
    },
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      void client.invalidateQueries({ queryKey: ['making-rules'] });
      reset();
      setError(null);
    },
    onError: setError,
  });
  const retire = useRetire('/making-rules', 'making-rules', setError);

  const describe = (r: MakingRule): string => {
    if (r.mode === 'SLAB') return `${r.slabs?.length ?? 0} weight slab(s)`;
    if (r.mode === 'NONE') return 'no charge';
    const v = Number(r.value ?? 0);
    if (r.mode === 'PCT_OF_METAL_VALUE' || r.mode === 'PCT_OF_TOTAL') return `${v}% · ${label(MAKING_MODES, r.mode)}`;
    return `${money(String(v))} · ${label(MAKING_MODES, r.mode)}`;
  };

  const ready =
    form.name.trim() !== '' &&
    (editing !== null || form.code.trim() !== '') &&
    (!needsValue || form.value !== '') &&
    (!isSlab || form.slabs.some((s) => s.ratePerGram !== ''));

  return (
    <>
      <ErrorNotice error={error ?? list.error} />
      <Layout
        list={
          <RowList
            loading={list.isLoading}
            rows={list.data?.data ?? []}
            empty="Add how your shop charges for making — for example ₹450 per gram of net weight."
            render={(r) => (
              <Row
                key={r.id}
                row={r}
                mayEdit={mayEdit}
                detail={describe(r)}
                onEdit={() => {
                  setEditing(r);
                  setForm({
                    code: r.code,
                    name: r.name,
                    mode: r.mode,
                    value: r.value ? String(Number(r.value)) : '',
                    min: r.min_amount ? String(Number(r.min_amount)) : '',
                    max: r.max_amount ? String(Number(r.max_amount)) : '',
                    slabs:
                      r.slabs && r.slabs.length > 0
                        ? r.slabs.map((s) => ({ fromWt: s.fromWt, toWt: s.toWt ?? '', ratePerGram: s.ratePerGram }))
                        : blank.slabs,
                    marginal: r.slab_marginal ?? false,
                  });
                }}
                onRetire={() => retire.ask(r)}
              />
            )}
          />
        }
        form={
          mayEdit ? (
            <FormCard
              title={editing ? `Edit “${editing.name}”` : 'Add a making charge'}
              onSubmit={() => save.mutate()}
              busy={save.isPending}
              ready={ready}
              editing={!!editing}
              onCancel={reset}
              submitLabel="Add making charge"
            >
              <Field label="Name" required>
                <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={120} placeholder="Chains ₹450/g" />
              </Field>
              <CodeField value={form.code} onChange={(v) => setForm({ ...form, code: v })} editing={!!editing} placeholder="MC-450" />
              <Field label="Charged as" required>
                <Select value={form.mode} onChange={(v) => setForm({ ...form, mode: v || 'PER_GRAM_NET' })} options={MAKING_MODES} />
              </Field>

              {needsValue ? (
                <Field label={isPct ? 'Percentage' : 'Amount'} required>
                  <NumberInput
                    value={form.value}
                    onChange={(v) => setForm({ ...form, value: v })}
                    decimals={2}
                    max={isPct ? 100 : undefined}
                    suffix={isPct ? '%' : '₹'}
                  />
                </Field>
              ) : null}

              {isSlab ? (
                <div className="space-y-2">
                  <p className="label">Slabs — ₹ per gram by the piece's weight</p>
                  {form.slabs.map((s, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
                      <NumberInput
                        value={s.fromWt}
                        decimals={3}
                        suffix="g"
                        placeholder="from"
                        onChange={(v) => setForm({ ...form, slabs: form.slabs.map((x, j) => (j === i ? { ...x, fromWt: v } : x)) })}
                      />
                      <NumberInput
                        value={s.toWt}
                        decimals={3}
                        suffix="g"
                        placeholder="up to"
                        onChange={(v) => setForm({ ...form, slabs: form.slabs.map((x, j) => (j === i ? { ...x, toWt: v } : x)) })}
                      />
                      <NumberInput
                        value={s.ratePerGram}
                        decimals={2}
                        suffix="₹/g"
                        placeholder="rate"
                        onChange={(v) =>
                          setForm({ ...form, slabs: form.slabs.map((x, j) => (j === i ? { ...x, ratePerGram: v } : x)) })
                        }
                      />
                      <button
                        type="button"
                        className="text-2xs text-ink-soft underline disabled:opacity-40"
                        disabled={form.slabs.length === 1}
                        onClick={() => setForm({ ...form, slabs: form.slabs.filter((_, j) => j !== i) })}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="text-2xs text-gold-700 underline"
                    onClick={() => {
                      const last = form.slabs[form.slabs.length - 1];
                      setForm({ ...form, slabs: [...form.slabs, { fromWt: last?.toWt ?? '', toWt: '', ratePerGram: '' }] });
                    }}
                  >
                    + Add a slab
                  </button>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.marginal} onChange={(e) => setForm({ ...form, marginal: e.target.checked })} />
                    Charge each slab's grams at its own rate (like income tax), not the whole piece at one rate
                  </label>
                </div>
              ) : null}

              {form.mode !== 'NONE' ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Minimum ₹" hint="Optional floor.">
                    <NumberInput value={form.min} onChange={(v) => setForm({ ...form, min: v })} decimals={2} suffix="₹" />
                  </Field>
                  <Field label="Maximum ₹" hint="Optional cap.">
                    <NumberInput value={form.max} onChange={(v) => setForm({ ...form, max: v })} decimals={2} suffix="₹" />
                  </Field>
                </div>
              ) : null}
            </FormCard>
          ) : null
        }
      />
      {retire.dialog}
    </>
  );
}

/* ═══════════════════════════════ wastage ════════════════════════════════ */

function WastageTab() {
  const { can } = usePermission();
  const mayEdit = can('master.manage');
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<WastageRule | null>(null);
  const blank = { code: '', name: '', mode: 'PCT_OF_NET', value: '' };
  const [form, setForm] = useState(blank);
  const list = useQuery({ queryKey: ['wastage-rules'], queryFn: () => api.get<{ data: WastageRule[] }>('/wastage-rules?limit=200') });

  const reset = (): void => {
    setForm(blank);
    setEditing(null);
  };
  const grams = form.mode === 'FLAT_GRAMS';
  const needsValue = form.mode !== 'NONE';

  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.name.trim(), mode: form.mode, value: needsValue ? form.value : '0' };
      if (editing) return api.patch(`/wastage-rules/${editing.id}`, body, editing.version);
      return api.post('/wastage-rules', {
        code: form.code.trim().toUpperCase(),
        name: body.name,
        mode: body.mode,
        value: body.value,
      });
    },
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      void client.invalidateQueries({ queryKey: ['wastage-rules'] });
      reset();
      setError(null);
    },
    onError: setError,
  });
  const retire = useRetire('/wastage-rules', 'wastage-rules', setError);

  const ready = form.name.trim() !== '' && (editing !== null || form.code.trim() !== '') && (!needsValue || form.value !== '');

  return (
    <>
      <ErrorNotice error={error ?? list.error} />
      <Layout
        list={
          <RowList
            loading={list.isLoading}
            rows={list.data?.data ?? []}
            empty="Add the wastage your shop charges — for example 8% of net weight."
            render={(r) => (
              <Row
                key={r.id}
                row={r}
                mayEdit={mayEdit}
                detail={
                  r.mode === 'NONE'
                    ? 'no wastage'
                    : `${Number(r.value ?? 0)}${r.mode === 'FLAT_GRAMS' ? ' g' : '%'} · ${label(WASTAGE_MODES, r.mode)}`
                }
                onEdit={() => {
                  setEditing(r);
                  setForm({ code: r.code, name: r.name, mode: r.mode, value: r.value ? String(Number(r.value)) : '' });
                }}
                onRetire={() => retire.ask(r)}
              />
            )}
          />
        }
        form={
          mayEdit ? (
            <FormCard
              title={editing ? `Edit “${editing.name}”` : 'Add a wastage'}
              onSubmit={() => save.mutate()}
              busy={save.isPending}
              ready={ready}
              editing={!!editing}
              onCancel={reset}
              submitLabel="Add wastage"
            >
              <Field label="Name" required>
                <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={120} placeholder="Wastage 8%" />
              </Field>
              <CodeField value={form.code} onChange={(v) => setForm({ ...form, code: v })} editing={!!editing} placeholder="WST-8" />
              <Field label="Worked out as" required>
                <Select value={form.mode} onChange={(v) => setForm({ ...form, mode: v || 'PCT_OF_NET' })} options={WASTAGE_MODES} />
              </Field>
              {needsValue ? (
                <Field label={grams ? 'Grams' : 'Percentage'} required>
                  <NumberInput
                    value={form.value}
                    onChange={(v) => setForm({ ...form, value: v })}
                    decimals={grams ? 3 : 2}
                    max={grams ? undefined : 100}
                    suffix={grams ? 'g' : '%'}
                  />
                </Field>
              ) : null}
            </FormCard>
          ) : null
        }
      />
      {retire.dialog}
    </>
  );
}

/* ═══════════════════════════ pricing rules ══════════════════════════════ */

function PricingTab() {
  const { can } = usePermission();
  const mayEdit = can('master.manage');
  const client = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<PricingRule | null>(null);
  const blank = { code: '', name: '', basis: 'PER_GRAM_AT_PURITY', appliesTo: 'SALE', making: '', wastage: '' };
  const [form, setForm] = useState(blank);

  const list = useQuery({ queryKey: ['pricing-rules'], queryFn: () => api.get<{ data: PricingRule[] }>('/pricing-rules?limit=200') });
  const making = useQuery({ queryKey: ['making-rules'], queryFn: () => api.get<{ data: MakingRule[] }>('/making-rules?limit=200') });
  const wastage = useQuery({ queryKey: ['wastage-rules'], queryFn: () => api.get<{ data: WastageRule[] }>('/wastage-rules?limit=200') });
  const makingName = new Map((making.data?.data ?? []).map((m) => [m.id, m.name]));
  const wastageName = new Map((wastage.data?.data ?? []).map((w) => [w.id, w.name]));

  const reset = (): void => {
    setForm(blank);
    setEditing(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        rate_basis: form.basis,
        applies_to: form.appliesTo,
        making_charge_rule_id: form.making || null,
        wastage_rule_id: form.wastage || null,
      };
      if (editing) return api.patch(`/pricing-rules/${editing.id}`, body, editing.version);
      return api.post('/pricing-rules', {
        code: form.code.trim().toUpperCase(),
        name: body.name,
        rate_basis: body.rate_basis,
        applies_to: body.applies_to,
        ...(form.making ? { making_charge_rule_id: form.making } : {}),
        ...(form.wastage ? { wastage_rule_id: form.wastage } : {}),
      });
    },
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      void client.invalidateQueries({ queryKey: ['pricing-rules'] });
      void client.invalidateQueries({ queryKey: ['pricingRules'] });
      reset();
      setError(null);
    },
    onError: setError,
  });
  const retire = useRetire('/pricing-rules', 'pricing-rules', setError);

  const activeOptions = (rows: Base[] | undefined, keep: string) =>
    (rows ?? []).filter((r) => r.is_active || r.id === keep).map((r) => ({ value: r.id, label: `${r.name} (${r.code})` }));

  const ready = form.name.trim() !== '' && (editing !== null || form.code.trim() !== '');

  return (
    <>
      <ErrorNotice error={error ?? list.error} />
      <Layout
        list={
          <RowList
            loading={list.isLoading}
            rows={list.data?.data ?? []}
            empty="A pricing rule pairs a making charge with a wastage. Make those first, then put them together here."
            render={(r) => (
              <Row
                key={r.id}
                row={r}
                mayEdit={mayEdit}
                detail={[
                  r.rate_basis === 'PER_FINE_GRAM' ? 'rate per pure gram' : 'rate per gram at purity',
                  r.applies_to === 'BOTH' ? 'sale & purchase' : r.applies_to.toLowerCase(),
                  r.making_charge_rule_id ? `making: ${makingName.get(r.making_charge_rule_id) ?? '—'}` : 'no making',
                  r.wastage_rule_id ? `wastage: ${wastageName.get(r.wastage_rule_id) ?? '—'}` : 'no wastage',
                ].join(' · ')}
                onEdit={() => {
                  setEditing(r);
                  setForm({
                    code: r.code,
                    name: r.name,
                    basis: r.rate_basis,
                    appliesTo: r.applies_to,
                    making: r.making_charge_rule_id ?? '',
                    wastage: r.wastage_rule_id ?? '',
                  });
                }}
                onRetire={() => retire.ask(r)}
              />
            )}
          />
        }
        form={
          mayEdit ? (
            <FormCard
              title={editing ? `Edit “${editing.name}”` : 'Add a pricing rule'}
              onSubmit={() => save.mutate()}
              busy={save.isPending}
              ready={ready}
              editing={!!editing}
              onCancel={reset}
              submitLabel="Add pricing rule"
            >
              <Field label="Name" required>
                <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={120} placeholder="Retail chains" />
              </Field>
              <CodeField value={form.code} onChange={(v) => setForm({ ...form, code: v })} editing={!!editing} placeholder="RETAIL" />
              <Field label="Making charge" hint="Leave empty for none.">
                <Select
                  value={form.making}
                  onChange={(v) => setForm({ ...form, making: v })}
                  options={activeOptions(making.data?.data, form.making)}
                  placeholder="No making charge"
                />
              </Field>
              <Field label="Wastage" hint="Leave empty for none.">
                <Select
                  value={form.wastage}
                  onChange={(v) => setForm({ ...form, wastage: v })}
                  options={activeOptions(wastage.data?.data, form.wastage)}
                  placeholder="No wastage"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Metal rate read as">
                  <Select
                    value={form.basis}
                    onChange={(v) => setForm({ ...form, basis: v || 'PER_GRAM_AT_PURITY' })}
                    options={[
                      { value: 'PER_GRAM_AT_PURITY', label: 'Per gram at purity (916 rate)' },
                      { value: 'PER_FINE_GRAM', label: 'Per pure gram (24K rate)' },
                    ]}
                  />
                </Field>
                <Field label="Used for">
                  <Select
                    value={form.appliesTo}
                    onChange={(v) => setForm({ ...form, appliesTo: v || 'SALE' })}
                    options={[
                      { value: 'SALE', label: 'Selling' },
                      { value: 'PURCHASE', label: 'Buying' },
                      { value: 'BOTH', label: 'Both' },
                    ]}
                  />
                </Field>
              </div>
            </FormCard>
          ) : null
        }
      />
      {retire.dialog}
    </>
  );
}
